import { logger } from './logger';

// --- Safari AdBlocker Bypass: Proxy Firebase Auth through First-Party Domain ---
// if (import.meta.env.PROD && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
//     const originalFetch = window.fetch;
//     window.fetch = async (...args) => {
//         try {
//             let [resource, config] = args;
//             
//             const rewriteUrl = (urlStr) => {
//                 if (urlStr.includes('identitytoolkit.googleapis.com')) {
//                     const urlObj = new URL(urlStr);
//                     return `/api/google-proxy/identity${urlObj.pathname}${urlObj.search}`;
//                 } else if (urlStr.includes('securetoken.googleapis.com')) {
//                     const urlObj = new URL(urlStr);
//                     return `/api/google-proxy/securetoken${urlObj.pathname}${urlObj.search}`;
//                 }
//                 return urlStr;
//             };
// 
//             if (typeof resource === 'string' || resource instanceof URL) {
//                 resource = rewriteUrl(resource.toString());
//             } else if (resource instanceof Request) {
//                 const newUrlStr = rewriteUrl(resource.url);
//                 if (newUrlStr !== resource.url) {
//                     // To safely clone request with new URL, we only explicitly pass what is safe or necessary
//                     resource = new Request(newUrlStr, {
//                         method: resource.method,
//                         headers: resource.headers,
//                         body: resource.body,
//                         credentials: resource.credentials,
//                         cache: resource.cache,
//                         redirect: resource.redirect,
//                         referrer: resource.referrer
//                     });
//                 }
//             }
//             return await originalFetch(resource, config);
//         } catch (err) {
//             return originalFetch(...args);
//         }
//     };
// }
// --------------------------------------------------------------------------------
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc, query, where, orderBy, limit, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth, signInWithEmailAndPassword, signOut as firebaseSignOut, GoogleAuthProvider, signInWithPopup, setPersistence, browserLocalPersistence, multiFactor, TotpMultiFactorGenerator, getMultiFactorResolver, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';

/**
 * Firebase Configuration for Oblique (Unified obliquelabs Project)
 */

const firebaseConfig = {
    apiKey:            (import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyAMlElgTG6HDN4o7gl3O5eqFQDx1-ZDm1Q')?.trim(),
    authDomain:        (import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'obliquelabs.firebaseapp.com')?.trim(),
    projectId:         (import.meta.env.VITE_FIREBASE_PROJECT_ID || 'obliquelabs')?.trim(),
    storageBucket:     (import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'obliquelabs.firebasestorage.app')?.trim(),
    messagingSenderId: (import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1028107219705')?.trim(),
    appId:             (import.meta.env.VITE_FIREBASE_APP_ID || '1:1028107219705:web:b519ec4e8ad7683bf22828')?.trim(),
};

const missingKeys = Object.entries(firebaseConfig)
    .filter(([, v]) => !v)
    .map(([k]) => k);

if (missingKeys.length > 0) {
    const errorMsg = `[Oblique] Missing Firebase config keys: ${missingKeys.join(', ')}. Please check your environment variables.`;
    console.error(errorMsg);
    // In production, we throw to prevent silent white screens
    if (import.meta.env.PROD) {
        throw new Error(errorMsg);
    }
} else {
    console.log('[Oblique] Firebase config keys validated successfully.');
}

// Initialize Firebase
let app;
try {
    console.log('[Oblique] Initializing Firebase app...');
    app = initializeApp(firebaseConfig);
    console.log('[Oblique] Firebase app initialized successfully.');
} catch (err) {
    console.error('[Oblique] Firebase initialization failed:', err);
    throw err;
}
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);


// Google provider
const googleProvider = new GoogleAuthProvider();

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (email, password) => {
    try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error('[Firebase] Sign in error:', error);
        if (error.code === 'auth/multi-factor-auth-required') {
            const resolverInfo = getMfaResolver(error);
            if (resolverInfo) return { success: false, mfaRequired: true, resolver: resolverInfo };
        }
        return { success: false, error: error.message };
    }
};

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
    try {
        const result = await signInWithPopup(auth, googleProvider);
        return { success: true, user: result.user };
    } catch (error) {
        console.error('[Firebase] Google sign in error:', error);
        if (error.code === 'auth/multi-factor-auth-required') {
            const resolverInfo = getMfaResolver(error);
            if (resolverInfo) return { success: false, mfaRequired: true, resolver: resolverInfo };
        }
        return { success: false, error: error.message };
    }
};

export const signOut = async () => {
    try {
        await firebaseSignOut(auth);
        return { success: true };
    } catch (error) {
        console.error('[Firebase] Sign out error:', error);
        return { success: false, error: error.message };
    }
};

export const sendVerificationEmail = async (user) => {
    try {
        await sendEmailVerification(user);
        return { success: true };
    } catch (error) {
        console.error('Error sending verification email:', error);
        return { success: false, error: error.message };
    }
};

export const resetPassword = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email);
        return { success: true };
    } catch (error) {
        console.error('Error sending password reset email:', error);
        return { success: false, error: error.message };
    }
};

export const getCurrentUser = () => {
    return auth?.currentUser || null;
};

// ============================================
// TOTP MFA FUNCTIONS
// ============================================

export const hasTotpMfa = (user) => {
    if (!user) return false;
    const enrolledFactors = multiFactor(user).enrolledFactors;
    return enrolledFactors.some(f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
};

export const getEnrolledFactors = (user) => {
    if (!user) return [];
    return multiFactor(user).enrolledFactors;
};

export const startTotpEnrollment = async (user) => {
    if (!user) throw new Error('User not authenticated');
    try {
        const multiFactorSession = await multiFactor(user).getSession();
        const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
        const qrCodeUrl = totpSecret.generateQrCodeUrl(user.email, 'Oblique');
        return { success: true, secret: totpSecret, secretKey: totpSecret.secretKey, qrCodeUrl };
    } catch (error) {
        console.error('[MFA] Start enrollment error:', error);
        return { success: false, error: error.message };
    }
};

export const completeTotpEnrollment = async (user, totpSecret, verificationCode, displayName = 'Authenticator') => {
    if (!user || !totpSecret) throw new Error('Invalid parameters');
    try {
        const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
        await multiFactor(user).enroll(multiFactorAssertion, displayName);
        return { success: true };
    } catch (error) {
        console.error('[MFA] Complete enrollment error:', error);
        return { success: false, error: error.code === 'auth/invalid-verification-code' ? 'Invalid code. Please try again.' : error.message };
    }
};

export const unenrollTotp = async (user) => {
    if (!user) throw new Error('User not authenticated');
    try {
        const enrolledFactors = multiFactor(user).enrolledFactors;
        const totpFactor = enrolledFactors.find(f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (totpFactor) {
            await multiFactor(user).unenroll(totpFactor);
            return { success: true };
        }
        return { success: false, error: 'No TOTP factor found' };
    } catch (error) {
        console.error('[MFA] Unenroll error:', error);
        return { success: false, error: error.message };
    }
};

export const getMfaResolver = (error) => {
    if (error.code !== 'auth/multi-factor-auth-required') return null;
    const resolver = getMultiFactorResolver(auth, error);
    return {
        resolver,
        hints: resolver.hints,
        hasTotpHint: resolver.hints.some(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID)
    };
};

export const completeMfaSignIn = async (resolver, verificationCode) => {
    try {
        const totpHint = resolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (!totpHint) return { success: false, error: 'No TOTP factor found' };
        const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, verificationCode);
        const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
        return { success: true, user: userCredential.user };
    } catch (error) {
        console.error('[MFA] Complete sign-in error:', error);
        return { success: false, error: error.code === 'auth/invalid-verification-code' ? 'Invalid code. Please try again.' : 'Verification failed. Please try again.' };
    }
};

/**
 * Case/Audit Services
 */
export const casesService = {
    subscribe(callback, filters = {}) {
        let q = collection(db, 'cases');
        if (filters.status) q = query(q, where('status', '==', filters.status));
        q = query(q, orderBy('createdAt', 'desc'));
        return onSnapshot(q, (snapshot) => {
            const cases = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(cases);
        });
    },
    async getOne(id) {
        const docSnap = await getDoc(doc(db, 'cases', id));
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    },
    async create(data) {
        return await addDoc(collection(db, 'cases'), { ...data, status: 'open', createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    },
    async update(id, data) {
        return await updateDoc(doc(db, 'cases', id), { ...data, updatedAt: serverTimestamp() });
    },
    async addNote(caseId, noteText, authorName) {
        const docRef = doc(db, 'cases', caseId);
        const caseDoc = await getDoc(docRef);
        const currentNotes = caseDoc.data()?.notes || [];
        return await updateDoc(docRef, {
            notes: [...currentNotes, {
                id: Date.now().toString(),
                text: noteText,
                author: authorName,
                createdAt: new Date().toISOString()
            }],
            updatedAt: serverTimestamp()
        });
    }
};

/**
 * Productivity Services
 */
export const leadsService = {
    subscribe(callback) {
        const q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
        return onSnapshot(q, (snapshot) => {
            const leads = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(leads);
        });
    },
    async create(data) {
        return await addDoc(collection(db, 'leads'), { ...data, status: 'new', auditStatus: 'pending', createdAt: serverTimestamp() });
    },
    async update(id, data) {
        return await updateDoc(doc(db, 'leads', id), { ...data, updatedAt: serverTimestamp() });
    },
    async audit(id, passed, notes = '') {
        return await updateDoc(doc(db, 'leads', id), {
            auditStatus: passed ? 'passed' : 'failed',
            auditNotes: notes,
            auditedAt: serverTimestamp()
        });
    }
};

export const dealsService = {
    subscribe(callback, filter = 'all') {
        let q = collection(db, 'deals');
        if (filter === 'pending') q = query(q, where('auditStatus', '==', 'pending'));
        else if (filter === 'completed') q = query(q, where('auditStatus', 'in', ['passed', 'failed']));
        q = query(q, orderBy('closedAt', 'desc'));
        return onSnapshot(q, (snapshot) => {
            const deals = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(deals);
        });
    },
    async create(data) {
        return await addDoc(collection(db, 'deals'), { ...data, auditStatus: 'pending', createdAt: serverTimestamp() });
    },
    async audit(id, passed, notes = '') {
        return await updateDoc(doc(db, 'deals', id), {
            auditStatus: passed ? 'passed' : 'failed',
            auditNotes: notes,
            auditedAt: serverTimestamp()
        });
    }
};

/**
 * Call Audits Collection
 */
export const callAuditsService = {
    subscribe(callback, filter = 'pending') {
        let q = collection(db, 'call_audits');
        if (filter === 'pending') q = query(q, where('audited', '==', false));
        else if (filter === 'no-recording') q = query(q, where('hasRecording', '==', false));
        else if (filter === 'long') q = query(q, where('duration', '>', 1200));
        return onSnapshot(q, (snapshot) => {
            const calls = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(calls);
        });
    },
    async audit(id, passed, notes = '') {
        return await updateDoc(doc(db, 'call_audits', id), {
            audited: true,
            passed,
            auditNotes: notes,
            auditedAt: serverTimestamp()
        });
    }
};

export const activitiesService = {
    subscribe(callback, limitCount = 50) {
        const q = query(collection(db, 'activities'), orderBy('createdAt', 'desc'), limit(limitCount));
        return onSnapshot(q, (snapshot) => {
            const activities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(activities);
        });
    },
    async log(type, description, metadata = {}) {
        return await addDoc(collection(db, 'activities'), { type, description, metadata, createdAt: serverTimestamp() });
    }
};

/**
 * SOP Alerts History Collection
 */
export const alertsService = {
    subscribe(callback, filters = {}) {
        let q = collection(db, 'sop_alerts');
        if (filters.ownerName) q = query(q, where('ownerName', '==', filters.ownerName));
        q = query(q, orderBy('notifiedAt', 'desc'));
        return onSnapshot(q, (snapshot) => {
            const alerts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            callback(alerts);
        });
    },
    async logAlert(leadData, complianceReason) {
        return await addDoc(collection(db, 'sop_alerts'), {
            leadId: leadData.id,
            leadName: leadData.name,
            ownerName: leadData.ownerName,
            ownerTeam: leadData.ownerTeam,
            reason: complianceReason,
            emailsSent: leadData.emailsSent,
            callsMade: leadData.callsMade,
            assignedDate: leadData.ownerAssignedDate || null,
            notifiedAt: serverTimestamp(),
            status: 'active'
        });
    }
};

export default { db, auth, casesService, leadsService, dealsService, callAuditsService, activitiesService, alertsService };
